import { useEffect, useMemo, useState } from "react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { LogoMark } from "@/components/Logo";
import { api } from "@/lib/api";

const FILES_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/files/`;

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/gallery")
      .then(({ data }) => setItems(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filters = useMemo(() => ["All", ...new Set(items.map((i) => i.category))], [items]);
  const visible = filter === "All" ? items : items.filter((i) => i.category === filter);
  const src = (item) => item.url || `${FILES_BASE}${item.storage_path}`;

  return (
    <main className="pt-16" data-testid="gallery-page">
      <section className="relative overflow-hidden noise-overlay crosshair-grid border-b border-gold/15">
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <p className="eyebrow mb-5">Field Records</p>
          <MaskedLines
            lines={["MUSTERS &", "MEMORIES."]}
            lineClassName="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight text-parchment"
          />
          <p className="mt-6 max-w-xl text-stone-300 text-sm sm:text-base leading-relaxed">
            Range days, fireside devotionals, and the fellowship between. Every frame a testimony
            of brothers and sisters sharpening one another.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        <div className="flex flex-wrap gap-2 mb-12" data-testid="gallery-filters">
          {filters.map((f) => (
            <button
              key={f}
              data-testid={`gallery-filter-${f.toLowerCase().replace(/ /g, "-")}`}
              onClick={() => setFilter(f)}
              className={`font-condensed uppercase tracking-[0.15em] text-sm px-4 py-2 border transition-colors duration-300 ${
                filter === f ? "bg-gold text-obsidian border-gold font-semibold" : "border-gold/25 text-stone-300 hover:border-crimson hover:text-crimson"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5" data-testid="gallery-loading">
            {[...Array(6)].map((_, i) => <div key={i} className="mb-5 aspect-[4/3] bg-card2/50 border border-gold/10 animate-pulse" />)}
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:balance]" data-testid="gallery-grid">
            {visible.map((g, i) => (
              <Reveal key={g.id} delay={(i % 3) * 0.07} className="mb-5 break-inside-avoid">
                  <figure className="group relative border border-gold/15 bg-card2/60 overflow-hidden" data-testid={`gallery-item-${i}`}>
                    <img src={src(g)} alt={g.caption} loading="lazy" className={`w-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out ${i % 3 === 0 ? "aspect-[3/4]" : "aspect-[4/3]"}`} />
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian/95 via-obsidian/20 to-transparent opacity-70 group-hover:opacity-95 transition-opacity duration-500" />
                    <div className="absolute inset-3 border border-gold/0 group-hover:border-gold/40 transition-colors duration-500 pointer-events-none" />
                    <figcaption className="absolute inset-x-0 bottom-0 p-5 translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-crimson">{g.category}</span>
                      <p className="font-condensed uppercase tracking-wider text-lg text-parchment mt-1 leading-tight">{g.caption}</p>
                    </figcaption>
                  </figure>
              </Reveal>
            ))}
          </div>
        )}

        <Reveal className="mt-16 text-center">
          <LogoMark className="w-8 h-8 text-gold mx-auto mb-4" />
          <p className="font-serif italic text-stone-400">"As iron sharpens iron, so one person sharpens another."</p>
          <p className="font-mono text-xs tracking-[0.3em] text-crimson uppercase mt-2">— Proverbs 27:17</p>
        </Reveal>
      </section>
    </main>
  );
}
